import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { FloorPlanPropTypes } from '../../../utils/DrupalEntityTypes/FloorPlan/FloorPlanPropTypes';

import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';

import FloorPlanImage from './FloorPlanImage';
import FloorPlanImagesWidgetButtons from './FloorPlanImagesWidgetButtons';

import './FloorPlanImagesWidget.scss';

const FloorPlanImagesWidget = ({
  className,
  data,
  minimalData,
  contentType,
}) => {
  const [activeFloorPlan, setActiveFloorPlan] = useState(false);
  const [hasFullData, setHasFullData] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Reset loading/display state when entity changes (e.g. different residence or floor plan)
  const entityKey = data?.uuid ?? data?.id ?? (data ? 'loaded' : 'none');
  useEffect(() => {
    setHasFullData(false);
    setIsImageLoaded(false);
  }, [entityKey]);

  // Track when full data arrives (only once per entity)
  useEffect(() => {
    if (data && !hasFullData) {
      setHasFullData(true);
    }
  }, [data, hasFullData]);

  // Use full data if available, otherwise use minimal data
  const dataToUse = data || minimalData;

  const {
    title,
    twoD: twoDProp,
    threeD: threeDProp,
    twoDFloorPlan,
    threeDFloorPlan,
  } = dataToUse || {};

  // Residence detail uses twoDFloorPlan/threeDFloorPlan; floor plan uses twoD/threeD
  const twoD = twoDProp ?? twoDFloorPlan;
  const threeD = threeDProp ?? threeDFloorPlan;

  // If we have full data, use modal (full size) images
  // If we only have minimal data, use oneThirdScreen (smaller) images temporarily
  const threeDSrc = hasFullData ? threeD?.modal : (threeD?.modal || threeD?.oneThirdScreen);
  const twoDSrc = hasFullData ? twoD?.modal : (twoD?.modal || twoD?.oneThirdScreen);

  const disclaimer = 'Renderings are intended as a general reference. Features, finishes, layout, and measurements may vary.';

  // Determine if we have any floor plan images to show
  const hasAnyFloorPlanImages = threeDSrc || twoDSrc;

  // Only treat as loaded when we have the actual endpoint response (data).
  const hasDataFromEndpoint = !!data;

  // Backend always includes twoD/threeD (or residence uses twoDFloorPlan/threeDFloorPlan) in the
  // full response; partial list data may not. Only show "no image" when we know the payload
  // included the floor plan section, so we don't flash "No Floor Plan Image Available" on residence
  // before the detail API returns.
  const dataHasFloorPlanSection = data && (
    Object.prototype.hasOwnProperty.call(data, 'twoD')
    || Object.prototype.hasOwnProperty.call(data, 'threeD')
    || Object.prototype.hasOwnProperty.call(data, 'twoDFloorPlan')
    || Object.prototype.hasOwnProperty.call(data, 'threeDFloorPlan')
  );

  // Show loading spinner until we know whether we have image data (and, if so, until loaded).
  // Preload images only when we have endpoint data.
  useEffect(() => {
    if (!hasDataFromEndpoint) {
      return;
    }

    // No image URLs yet. Only show "no image" once we know the response included the floor plan
    // section (avoids flash on residence when data is partial). Short delay for smooth transition.
    if (!hasAnyFloorPlanImages) {
      if (!dataHasFloorPlanSection) {
        return; // Keep spinner until full data with floor plan keys arrives
      }
      const id = window.setTimeout(() => {
        setIsImageLoaded(true);
      }, 200);
      return () => window.clearTimeout(id);
    }

    // We have image URLs — preload then show
    setIsImageLoaded(false);

    const imagesToLoad = [threeDSrc, twoDSrc].filter(Boolean);
    let loadedCount = 0;
    let rafId;

    const handleLoad = () => {
      loadedCount += 1;
      if (loadedCount === imagesToLoad.length) {
        rafId = requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsImageLoaded(true);
          });
        });
      }
    };

    const images = imagesToLoad.map((src) => {
      const img = new Image();
      img.onload = handleLoad;
      img.onerror = handleLoad;
      img.src = src;
      return img;
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      images.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [hasDataFromEndpoint, dataHasFloorPlanSection, threeDSrc, twoDSrc, hasAnyFloorPlanImages]);

  // Show spinner while we don't know if we have image data (no data yet), or until we're done (no images / preload done).
  const showLoadingSpinner = !hasDataFromEndpoint || !isImageLoaded;

  return (
    <div
      id="floorplan-scroll"
      className={className || ''}
    >
      <div className="floorPlanImagesWidget">
        {/* Stacking container for crossfade between loading and content */}
        <div
          className={classnames('floorPlanImagesWidget__stackingContainer', {
            'floorPlanImagesWidget__stackingContainer--loading': showLoadingSpinner,
          })}
        >
          {/* Loading wrapper - hide with CSS when not loading */}
          <div
            className={classnames('floorPlanImagesWidget__loadingWrapper', {
              'floorPlanImagesWidget__loadingWrapper--hidden': !showLoadingSpinner,
            })}
          >
            <LoadingSpinner />
          </div>

          {/* Images wrapper */}
          <div
            className={classnames('floorPlanImagesWidget__imagesWrapper', {
              'floorPlanImagesWidget__imagesWrapper--hidden': showLoadingSpinner,
            })}
          >
            {hasAnyFloorPlanImages ? (
              <div className="floorPlanImagesWidget__images">
                <FloorPlanImage
                  src={threeDSrc}
                  imgClassName={classnames(
                    'floorPlanImage__image',
                    { 'floorPlanImage__image--inactive': activeFloorPlan },
                  )}
                  imgAlt={`3D ${title}`}
                  disclaimer={disclaimer}
                />

                <FloorPlanImage
                  src={twoDSrc}
                  imgClassName={classnames(
                    'floorPlanImage__image',
                    { 'floorPlanImage__image--inactive': !activeFloorPlan && threeDSrc },
                  )}
                  imgAlt={`2D ${title}`}
                  disclaimer={disclaimer}
                />
              </div>
            ) : data ? (
              <div className="floorPlanImagesWidget__noImagePlaceholder">
                No Floor Plan Image Available
              </div>
            ) : null}
          </div>
        </div>

        {/* We pass all the data to the floor plan widget because
        it has a print link that needs fp data. */}
        {!showLoadingSpinner && (
          <FloorPlanImagesWidgetButtons
            activeFloorPlan={activeFloorPlan}
            setActiveFloorPlan={setActiveFloorPlan}
            data={dataToUse}
            contentType={contentType}
          />
        )}
      </div>

      {(twoDSrc || threeDSrc) && (
        <div className="detail__footnote">
          {disclaimer}
        </div>
      )}
    </div>
  );
};

FloorPlanImagesWidget.propTypes = {
  className: PropTypes.string,
  data: FloorPlanPropTypes.data,
  minimalData: FloorPlanPropTypes.data,
  contentType: PropTypes.string,
};

FloorPlanImagesWidget.defaultProps = {
  className: '',
  data: null,
  minimalData: null,
  contentType: '',
};

export default FloorPlanImagesWidget;
